import isObject from 'lodash/isObject';
import 'rxjs/add/observable/merge';
import {Observable} from 'rxjs/Observable';
import {applyAutoFilter, ReduxList} from '../../_libs/redux-observable-list';
import {getProjectReduxItem} from '../../app/model/reducers/state';
import {buildParentChannelSyncNotifier, buildUserChannelSyncNotifier} from '../../auth/utils/redux-lists';
import {BASE_REDUX_LIST_OPTIONS} from '../../config/redux-list/index';
import {getCollection} from '../../core-utilities/domain/ReduxUtils';
import {FilterModalOptions} from '../../core-utilities/filters/FilterModalOptions';
import {applyDefaultReduxListComponentOptions} from '../../core-utilities/redux/ReduxListContainer';
import {SYNC_MODEL_PATH, SYNC_PATH} from '../../core-utilities/rx-utils';
import {DateFilterDialogController} from '../../priorities/filters/DateFilterDialogController';
import {removeByUserAssignment} from '../../priorities/redux/prioritiesListConfig';
import {MILESTONE_COLLECTION} from '../../projects/domain/config';
import {TaskType} from '../enums/TaskType';
import filterTemplate from '../filters/tasks-list-filter.html';
import {removeByAssignmentStatus, removeByStatus} from '../filters/TasksFilterService';
import {logger} from './logger';
import {buildReducers, isVisibleByMilestone, listToArray} from './utils';


/**
 * @param {Object} config
 * @param {TaskType} config.taskType
 * @param {String} config.prefix
 * @param {Function} config.getState
 * @param {String} config.parentPath
 * @param {String} config.resourcePath
 * @param {String} config.childPath
 * @return {{defaultFilterOptions: Object, filterModalOptions: *, reduxList: ReduxList, componentOptions: ReduxListComponentOptions, isVisible: *}}
 */
export function makeTaskList(config) {
  const {
    taskType,
    prefix,
    getState,
    parentPath,
    resourcePath,
    childPath,
  } = config;

  const defaultFilterOptions = taskType !== TaskType.TEMPLATE ? {
    user: {
      assignedToAny: true,
      // assignedByUser: true,
      assignedToNone: true,
    },
    status: {
      cancelled: true,
      has_rejection: true,
      rejected: true,
      pending: true,
      in_progress: true,
      submitted: true,
      approved: true,
    },
    calStat: {
      complete: true,
      cancelled: true,
      past_due: true,
      imminent: true,
      scheduled: true,
      not_scheduled: true,
    },
    dateRange: {
      useAfter: false,
      // afterDate: new Date(),
      useBefore: false,
      // beforeDate: new Date(),
    },
    milestone: {
      // Filters out specified milestones by ID
      id: {
        // Toggle all to (un)collapse all milestones
        // all: false,
      },
    },
  } : {
    milestone: {
      // Filters out specified milestones by ID
      id: {
        // Toggle all to (un)collapse all milestones
        // all: false,
      },
    },
  };

  const filterModalOptions = taskType !== TaskType.TEMPLATE ? new FilterModalOptions({
    filterTemplate,
    controller: DateFilterDialogController,
    filterOptions: defaultFilterOptions,
  }) : null;

  const matchesAutoFilter = applyAutoFilter(['name', 'description'], 1);

  const isVisible = taskType !== TaskType.TEMPLATE ? isProjectTaskVisible : isTemplateTaskVisible;

  const syncNotifier = getSyncNotifier(taskType, parentPath, childPath);

  /** @type {ReduxList} */
  const reduxList = new ReduxList({
    ...BASE_REDUX_LIST_OPTIONS,
    prefix,
    getState,
    pageSize: 100,
    hasParentId: true,
    useDelete: true,
    useFiltering: true,
    useAutoFilter: true,
    useFilterOptions: true,
    useSortOptions: false,
    listToArray,
    defaultFilterOptions,
    isVisible,
    syncNotifier,
    syncModelPath: parentId => `${parentPath}/${parentId}/${childPath}/${SYNC_MODEL_PATH}`,
    listPath: parentId => `${parentPath}/${parentId}/${childPath}`,
    listSyncPath: parentId => `${parentPath}/${parentId}/${childPath}/${SYNC_PATH}`,
    deleteItemPath: deleteItemPath,
    logger,

    // cacheOptions: {
    //   localForage: asyncStorage,
    //   key: targetOptions.cacheName,
    // },
  });

  reduxList.reducers.reducer = buildReducers(reduxList);

  // TODO: finish config
  const componentOptions = applyDefaultReduxListComponentOptions(reduxList.options, {
    excludeFilterModalOptions: taskType === TaskType.TEMPLATE,
    filterModalOptions,
  });

  function isProjectTaskVisible(item, filterData) {
    if (!isObject(item)) return false;

    // Ignore Milestones
    if (getCollection(item) === MILESTONE_COLLECTION)
      return true;

    if (!isVisibleByMilestone(item, filterData)) return false;
    if (!matchesAutoFilter(item, filterData.filter)) return false;
    if (removeByStatus(item, filterData.filterOptions)) return false;
    if (removeByAssignmentStatus(item, filterData.filterOptions)) return false;
    if (removeByUserAssignment(item, filterData.filterOptions/* , rootState*/)) return false;
    return true;
  }

  function isTemplateTaskVisible(item, filterData) {
    if (!isObject(item)) return false;

    // Ignore Milestones
    if (getCollection(item) === MILESTONE_COLLECTION)
      return true;

    if (!isVisibleByMilestone(item, filterData)) return false;
    if (!matchesAutoFilter(item, filterData.filter)) return false;
    return true;
  }

  function deleteItemPath(itemId, payload) {
    switch (payload.collection) {
      case MILESTONE_COLLECTION:
        return `${MILESTONE_COLLECTION}/${itemId}`;

      default:
        return `${resourcePath}/${itemId}`;
    }
  }

  return {
    defaultFilterOptions,
    filterModalOptions,
    reduxList,
    componentOptions,
    isVisible,
  };
}

function getSyncNotifier(taskType, parentPath, childPath) {
  switch (taskType) {
    case TaskType.ASSET:
      return buildAssetTaskChannelSyncNotifier(parentPath, childPath);
    case TaskType.PROJECT:
      return buildProjectTaskChannelSyncNotifier(parentPath, childPath);
    case TaskType.TEMPLATE:
      return buildTemplateTaskChannelSyncNotifier(parentPath, childPath);
  }
}

function buildProjectTaskChannelSyncNotifier(parentPath, childPath) {
  const buildParentNotifier = buildParentChannelSyncNotifier(parentPath, childPath);
  const buildProjectMilestonesNotifier = buildParentChannelSyncNotifier('projects', 'project-milestones');
  const buildPrioritiesSyncNotifier = buildUserChannelSyncNotifier('priorities');
  return (parentId, store) => {
    return Observable
      .merge(
        buildParentNotifier(parentId, store),
        buildProjectMilestonesNotifier(parentId, store),
        buildPrioritiesSyncNotifier(null, store),
      );
  };
}

function buildTemplateTaskChannelSyncNotifier(parentPath, childPath) {
  const buildParentNotifier = buildParentChannelSyncNotifier(parentPath, childPath);
  const buildTemplateMilestonesNotifier = buildParentChannelSyncNotifier('projects', 'template-milestones');
  const buildPrioritiesSyncNotifier = buildUserChannelSyncNotifier('priorities');
  return (parentId, store) => {
    return Observable
      .merge(
        buildParentNotifier(parentId, store),
        buildTemplateMilestonesNotifier(parentId, store),
        buildPrioritiesSyncNotifier(null, store),
      );
  };
}

function buildAssetTaskChannelSyncNotifier(parentPath, childPath) {
  // Watching the project is used to detect changes to templates like to milestones
  const buildTemplateTasksNotifier = buildParentChannelSyncNotifier('projects', 'template-tasks');
  const buildTemplateMilestonesNotifier = buildParentChannelSyncNotifier('projects', 'template-milestones');
  const buildParentNotifier = buildParentChannelSyncNotifier(parentPath, childPath);
  const buildPrioritiesSyncNotifier = buildUserChannelSyncNotifier('priorities');
  return (parentId, store) => {
    const parentNotifier$ = buildParentNotifier(parentId, store);
    const prioritiesNotifier$ = buildPrioritiesSyncNotifier(null, store);
    const projectModel = getProjectReduxItem(store.getState());

    if (projectModel.id) {
      return Observable
        .merge(
          parentNotifier$,
          prioritiesNotifier$,
          buildTemplateTasksNotifier(projectModel.id, store),
          buildTemplateMilestonesNotifier(projectModel.id, store),
        );

    } else {
      return Observable
        .merge(
          parentNotifier$,
          prioritiesNotifier$,
        );
    }
  };
}



// WEBPACK FOOTER //
// ./src/js/tasks/redux/configTasks.js